// @ts-check
/**
 * Wholesale tracker constants and small pure helpers for the admin screens.
 *
 * The enum values below mirror /shared/api-constants.js (WHOLESALE_*). The admin build cannot import
 * that file (Create React App refuses imports from outside src), so utils/wholesale.test.js compares
 * these copies to the shared file and fails if they drift.
 */

export const WHOLESALE_STAGE = Object.freeze({
  NOT_CONTACTED: 'not_contacted',
  CONTACTED: 'contacted',
  REPLIED: 'replied',
  SAMPLES_OR_VISIT: 'samples_or_visit',
  QUOTE_RECEIVED: 'quote_received',
  NEGOTIATING: 'negotiating',
  AGREED: 'agreed',
  SIGNED: 'signed',
  LIVE: 'live',
  PARKED: 'parked',
  DECLINED: 'declined',
});

export const WHOLESALE_PARTNER_KIND = Object.freeze({
  BOUTIQUE: 'boutique',
  DEPARTMENT_STORE: 'department_store',
  MARKETPLACE: 'marketplace',
  CURATOR: 'curator',
  FESTIVAL: 'festival',
  VENUE: 'venue',
  CLUB: 'club',
  NETWORK: 'network',
  AMPLIFIER: 'amplifier',
});

export const WHOLESALE_MODEL = Object.freeze({
  SAMPLE_RAIL: 'sample_rail',
  SALE_OR_RETURN: 'sale_or_return',
  OUTRIGHT: 'outright',
});

export const WHOLESALE_PRICE_TIER = Object.freeze({
  LOWER: 'lower',
  MID: 'mid',
  UPPER: 'upper',
  UNKNOWN: 'unknown',
});

export const WHOLESALE_CAPSULE_TIER = Object.freeze({
  LOWER: 'lower',
  UPPER: 'upper',
  BOTH: 'both',
});

export const WHOLESALE_PRIORITY = Object.freeze({
  NOW: 'now',
  NEXT: 'next',
  JANUARY: 'january',
});

export const WHOLESALE_CHANNEL = Object.freeze({
  EMAIL: 'email',
  WHATSAPP: 'whatsapp',
  INSTAGRAM: 'instagram',
  PHONE: 'phone',
  IN_PERSON: 'in_person',
  WEB_FORM: 'web_form',
});

export const WHOLESALE_DIRECTION = Object.freeze({
  OUTBOUND: 'outbound',
  INBOUND: 'inbound',
});

export const WHOLESALE_EXCLUSIVITY = Object.freeze({
  NONE: 'none',
  CITY: 'city',
  REGION: 'region',
  NATIONAL: 'national',
});

export const WHOLESALE_MARKDOWN_RIGHTS = Object.freeze({
  NONE: 'none',
  WITH_CONSENT: 'with_consent',
  UNILATERAL: 'unilateral',
});

export const WHOLESALE_QUOTE_STATUS = Object.freeze({
  OPEN: 'open',
  SUPERSEDED: 'superseded',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
});

export const WHOLESALE_AGREEMENT_STATUS = Object.freeze({
  DRAFT: 'draft',
  ACTIVE: 'active',
  EXPIRED: 'expired',
  TERMINATED: 'terminated',
});

export const WHOLESALE_PLACEMENT_KIND = Object.freeze({
  SAMPLES: 'samples',
  CONSIGNMENT: 'consignment',
  OUTRIGHT_DELIVERY: 'outright_delivery',
});

export const WHOLESALE_OUTREACH_STATUS = Object.freeze({
  SENDING: 'sending',
  QUEUED: 'queued',
  SENT: 'sent',
  FAILED: 'failed',
});

export const WHOLESALE_OUTREACH_KIND = Object.freeze({
  FIRST: 'first',
  NUDGE: 'nudge',
  FINAL: 'final',
});

export const WHOLESALE_REMINDER_KIND = Object.freeze({
  DAY_4_NUDGE: 'day_4_nudge',
  DAY_10_CALL: 'day_10_call',
  QUOTE_UNANSWERED: 'quote_unanswered',
  FOLLOW_UP: 'follow_up',
  RE_CONTACT: 're_contact',
  AGREEMENT_ENDING: 'agreement_ending',
});

/** Stage order, as the board columns read left to right. */
export const STAGE_ORDER = Object.values(WHOLESALE_STAGE);

const LABEL_OVERRIDES = {
  samples_or_visit: 'Samples or visit',
  sample_rail: 'A. Sample rail',
  sale_or_return: 'B. Sale or return',
  outright: 'C. Outright',
  day_4_nudge: 'Day-4 nudge',
  day_10_call: 'Day-10 call',
  in_person: 'In person',
  whatsapp: 'WhatsApp',
  january: 'Hold for January',
};

/** 'quote_received' -> 'Quote received' */
export const label = (value) => {
  if (!value) return '';
  if (LABEL_OVERRIDES[value]) return LABEL_OVERRIDES[value];
  const text = value.replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const STAGE_BADGE = {
  not_contacted: 'bg-muted text-muted-foreground',
  contacted: 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300',
  replied: 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300',
  samples_or_visit: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300',
  quote_received: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300',
  negotiating: 'bg-orange-100 text-orange-800 dark:bg-orange-950/60 dark:text-orange-300',
  agreed: 'bg-lime-100 text-lime-800 dark:bg-lime-950/60 dark:text-lime-300',
  signed: 'bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-300',
  live: 'bg-emerald-200 text-emerald-900 dark:bg-emerald-900/60 dark:text-emerald-200',
  parked: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  declined: 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300',
};

/** 'Rs 11,751', or a dash when there is no number. */
export const formatRupees = (value) =>
  value === null || value === undefined ? '\u2014' : `\u20B9${Math.round(Number(value)).toLocaleString('en-IN')}`;

/** '2026-10-19' -> '19 Oct 2026'. Date-only strings, so no timezone shift. */
export const formatDay = (iso) => {
  if (!iso) return '\u2014';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return `${d} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]} ${y}`;
};

/**
 * The badge for a partner's nearest deadline (fields computed by the server: days_to_deadline,
 * deadline_kind 'apply_by' | 'event', deadline_passed). Null when there is nothing to show.
 * Red at 7 days or fewer, amber at 8-21, neutral beyond; "Date passed" is muted.
 * @param {{days_to_deadline?: number|null, deadline_kind?: string|null, deadline_passed?: boolean}} item
 * @returns {{text: string, className: string}|null}
 */
export const deadlineBadge = (item) => {
  const days = item.days_to_deadline;
  if (days === null || days === undefined) {
    return item.deadline_passed ? { text: 'Date passed', className: 'bg-muted text-muted-foreground' } : null;
  }
  const what = item.deadline_kind === 'apply_by' ? 'Apply by' : 'Event';
  let text = `${what} in ${days} ${days === 1 ? 'day' : 'days'}`;
  if (days === 0) text = `${what} today`;
  let className = 'bg-muted text-foreground';
  if (days <= 7) className = 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300';
  else if (days <= 21) className = 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300';
  return { text, className };
};

export const formatDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '\u2014';

/** One line describing a quote's terms, for the ranking and the quotes list. */
export const termsSummary = (q) => {
  const parts = [];
  if (q.model === WHOLESALE_MODEL.OUTRIGHT) parts.push(`${q.wholesale_pct_of_tag}% of tag`);
  else parts.push(`${q.store_share_pct}% share`);
  parts.push(
    q.model === WHOLESALE_MODEL.OUTRIGHT && q.payment_days === 0 ? 'paid on delivery' : `${q.payment_days} days`,
  );
  if (q.window_weeks) parts.push(`${q.window_weeks}-week window`);
  if (q.exclusivity && q.exclusivity !== 'none') parts.push(`${q.exclusivity} exclusivity`);
  return parts.join(', ');
};

/** The share, in plain terms, for a partner row: 'B' for sale or return etc. */
export const modelLetter = (model) => ({ sample_rail: 'A', sale_or_return: 'B', outright: 'C' })[model] || '';

/**
 * The next 5 January, as YYYY-MM-DD, counting "today" in India (IST, no DST): this year's 5 January if it is still
 * ahead, otherwise next year's. Used to park a partner who has had the final follow-up.
 * @param {Date} [now]
 * @returns {string}
 */
export const nextJanuaryFifth = (now = new Date()) => {
  const ist = new Date(now.getTime() + (5 * 60 + 30) * 60 * 1000);
  const year = ist.getUTCFullYear();
  const today = Date.UTC(year, ist.getUTCMonth(), ist.getUTCDate());
  return `${today < Date.UTC(year, 0, 5) ? year : year + 1}-01-05`;
};

export const PARK_NEXT_ACTION = 'Re-contact in January';
